import registerCustomElement from './registerCustomElement';
import createVueInstance from './createVueInstance';
import { getProps } from './props';
import { camelize } from './helpers';

/**
 * Copied from vue-custom-element
 * And modified to enable default-like behavior of parsing json data automatically,
 * when properties have prefixed with a double-colon.
 */

/**
 * Number and Boolean props are treated as strings
 * We should convert it so props will behave as intended
 * Conversion can be overwritted by prop validation (https://vuejs.org/v2/guide/components-props.html#Prop-Validation)
 * @param value
 * @param overrideType
 * @param jsonParse
 * @returns {*}
 */
export function convertAttributeValue(value, overrideType, jsonParse = false) {
    let propsValue = value;
    const isBoolean = ['true', 'false'].indexOf(value) > -1;
    const valueParsed = parseFloat(propsValue, 10);
    const isNumber = !isNaN(valueParsed) && isFinite(propsValue) && (typeof propsValue === 'string' && !propsValue.match(/^0+[^.]\d*$/g));

    if (jsonParse) {
        return JSON.parse(value);
    }

    if (overrideType && overrideType !== Boolean && typeof propsValue !== overrideType) { // eslint-disable-line valid-typeof
        propsValue = overrideType(value);
    } else if (isBoolean || overrideType === Boolean) {
        propsValue = propsValue === '' ? true : propsValue === 'true';
    } else if (isNumber) {
        propsValue = valueParsed;
    }

    return propsValue;
}


/**
 * In root Vue instance we should initialize props as 'propsData'.
 * @param instanceOptions
 * @param componentDefinition
 * @param props
 */
export function getPropsData(element, componentDefinition, props) {
    const propsData = componentDefinition.propsData || {};

    props.hyphenate.forEach((name, index) => {
        const propCamelCase = props.camelCase[index];
        const propValue = element.attributes[name] || element.attributes[':'+name] || element[propCamelCase];

        let type = null;
        if (props.types[propCamelCase]) {
            type = props.types[propCamelCase];
        }

        const jsonParse = element.attributes.hasOwnProperty(':'+name);
        // ensure propsData is only set if `propsValue` exists.
        if (propValue instanceof Attr) {
            propsData[propCamelCase] = convertAttributeValue(propValue.value, type, jsonParse);
        } else if (typeof propValue !== 'undefined') {
            propsData[propCamelCase] = propValue;
        }
    });

    return propsData;
}

export default function install(Vue) {
    Vue.customElement = function vueCustomElement(tag, componentDefinition, options = {}) {
        const isAsyncComponent = typeof componentDefinition === 'function';
        const optionsProps = isAsyncComponent && { props: options.props || [] };

        const props = getProps(isAsyncComponent ? optionsProps : componentDefinition);

        // register Custom Element
        const CustomElement = registerCustomElement(tag, {
            constructorCallback() {
                typeof options.constructorCallback === 'function' && options.constructorCallback.call(this);
            },

            connectedCallback() {
                const asyncComponentPromise = isAsyncComponent && componentDefinition();
                const isAsyncComponentPromise = asyncComponentPromise && asyncComponentPromise.then && typeof asyncComponentPromise.then === 'function';

                typeof options.connectedCallback === 'function' && options.connectedCallback.call(this);

                if (isAsyncComponent && !isAsyncComponentPromise) {
                    throw new Error(`Async component ${tag} do not returns Promise`);
                }
                if (!this.__detached__) {
                    if (isAsyncComponentPromise) {
                        asyncComponentPromise.then((lazyLoadedComponent) => {
                            const lazyLoadedComponentProps = getProps(lazyLoadedComponent);
                            createVueInstance(this, Vue, lazyLoadedComponent, lazyLoadedComponentProps, options);
                            typeof options.vueInstanceCreatedCallback === 'function' && options.vueInstanceCreatedCallback.call(this);
                        });
                    } else {
                        createVueInstance(this, Vue, componentDefinition, props, options);
                        typeof options.vueInstanceCreatedCallback === 'function' && options.vueInstanceCreatedCallback.call(this);
                    }
                }

                this.__detached__ = false;
            },

            /**
             *  When using element in e.g. modal, it's detached and then attached back to document.
             *  It will be unfortunate if we will destroy Vue instance when it happens.
             *  That's why we detect if it's permament using setTimeout
             */
            disconnectedCallback() {
                this.__detached__ = true;
                typeof options.disconnectedCallback === 'function' && options.disconnectedCallback.call(this);

                options.destroyTimeout !== null && setTimeout(() => {
                    if (this.__detached__ && this.__vue_custom_element__) {
                        this.__vue_custom_element__.$destroy(true);
                        delete this.__vue_custom_element__;
                        delete this.__vue_custom_element_props__;
                    }
                }, options.destroyTimeout || 3000);
            },

            /**
             * When attribute changes we should update Vue instance
             * @param name
             * @param oldVal
             * @param value
             */
            attributeChangedCallback(name, oldValue, value) {
                const parseJson = name.substr(0, 1) === ':';

                if (this.__vue_custom_element__ && typeof value !== 'undefined') {
                    const nameCamelCase = camelize(name);
                    typeof options.attributeChangedCallback === 'function' && options.attributeChangedCallback.call(this, name, oldValue, value);
                    const type = this.__vue_custom_element_props__.types[nameCamelCase];
                    this.__vue_custom_element__[nameCamelCase] = convertAttributeValue(value, type, parseJson);
                }
            },

            observedAttributes: props.hyphenate,

            shadow: !!options.shadow && !!HTMLElement.prototype.attachShadow
        });

        return CustomElement;
    };
}
